import * as React from 'react';

import { useDrawerHistory } from '@/hooks/use-drawer-history';
import { cn } from '@/lib/shadcn/lib/utils';
import { Drawer as DrawerPrimitive } from 'vaul';

export type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  disableHistory?: boolean;
};

const Drawer = ({
  shouldScaleBackground = false,
  open,
  defaultOpen,
  onOpenChange,
  disableHistory,
  ...props
}: DrawerProps) => {
  const historyProps = useDrawerHistory({
    open,
    defaultOpen,
    onOpenChange,
    disableHistory,
  });

  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      {...props}
      {...historyProps}
    />
  );
};
Drawer.displayName = 'Drawer';

export type DrawerNestedRootProps = React.ComponentProps<
  typeof DrawerPrimitive.NestedRoot
> & {
  disableHistory?: boolean;
};

const DrawerNestedRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  disableHistory,
  ...props
}: DrawerNestedRootProps) => {
  const historyProps = useDrawerHistory({
    open,
    defaultOpen,
    onOpenChange,
    disableHistory,
  });

  return <DrawerPrimitive.NestedRoot {...props} {...historyProps} />;
};
DrawerNestedRoot.displayName = 'DrawerNestedRoot';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-[1000] bg-black-50', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    overlayClassName?: string;
  }
>(({ className, overlayClassName = '', children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay className={`${overlayClassName}`} />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[1000] flex h-auto max-h-[97dvh] flex-col overscroll-contain rounded-t-large bg-white',
        className,
      )}
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

const DrawerHandle = DrawerPrimitive.Handle;

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex items-center justify-between px-unit-8 pb-unit-4', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-h5 font-semibold text-slate-500', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-p4 text-slate-400', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerNestedRoot,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHandle,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
